import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchAnalytics, fetchCountryGrowth, type AdminAnalytics, type CountryRow } from '@/lib/adapters'
import { ErrorState, Skeleton } from '@/components/States'
import { BarChart, type BarDatum } from '@/components/BarChart'
import { SmsSummaryCard } from '@/screens/analytics/SmsSummaryCard'

/**
 * A2.06 — O'sish dashboard'i. `/admin/analytics` (ro'yxatdan o'tish/kunlik xabar/
 * faollashuv funnel) + `/admin/countries` (davlat bo'yicha ro'yxatdan o'tish) —
 * o'sishga fokuslangan JONLI ko'rinish. Yangi endpoint kerak emas.
 */
export function GrowthDashboard() {
  const t = useT()
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null)
  const [countries, setCountries] = useState<CountryRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    Promise.all([fetchAnalytics(ac.signal), fetchCountryGrowth(ac.signal)])
      .then(([a, c]) => {
        if (!alive) return
        setAnalytics(a)
        setCountries(c)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [reload])

  const msgBars = useMemo<BarDatum[]>(
    () =>
      (analytics?.messages_per_day ?? []).map((d) => ({
        label: d.day.slice(5),
        value: d.messages,
        title: d.day,
      })),
    [analytics],
  )

  const topCountries = countries.slice(0, 12)
  const maxUsers = topCountries.length ? topCountries[0]!.users : 0
  const rate = analytics ? `${(analytics.funnel.activation_rate * 100).toFixed(1)}%` : undefined

  if (error) return <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />

  return (
    <>
      <PageHeader title={t.dash.growthTitle} screenId="A2.06" />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="grid grid-cols-2 gap-2 md:grid-cols-3 lg:grid-cols-5">
          <KPI label={t.dash.regToday} value={analytics?.registrations.today} loading={loading} accent />
          <KPI label={t.dash.regD7} value={analytics?.registrations.d7} loading={loading} />
          <KPI label={t.dash.regD30} value={analytics?.registrations.d30} loading={loading} />
          <KPI label={t.dash.dau} value={analytics?.dau} loading={loading} />
          <KPI label={t.dash.mau} value={analytics?.mau} loading={loading} />
        </div>

        <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
          <Panel title={t.dash.messagesPerDay}>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <BarChart data={msgBars} emptyLabel={t.dash.noData} tableLabel={t.dash.valuesTable} />
            )}
          </Panel>

          <Panel title={t.dash.activation}>
            {loading ? (
              <Skeleton className="h-40 w-full" />
            ) : (
              <div className="flex h-40 flex-col justify-center">
                <div className="font-mono text-3xl tabular text-accent">{rate}</div>
                <div className="mt-2 text-sm text-tertiary">
                  {analytics
                    ? t.dash.activationHint(
                        analytics.funnel.sent_first_message,
                        analytics.funnel.total_users,
                      )
                    : ''}
                </div>
              </div>
            )}
          </Panel>
        </div>

        <div className="mt-3">
          <Panel title={t.dash.topCountries}>
            {loading ? (
              <div className="space-y-2">
                {Array.from({ length: 8 }, (_, i) => (
                  <Skeleton key={i} className="h-5 w-full" />
                ))}
              </div>
            ) : !topCountries.length ? (
              <div className="py-6 text-center text-sm text-tertiary">{t.dash.noData}</div>
            ) : (
              <div className="space-y-1.5">
                {topCountries.map((c) => (
                  <div key={c.code} className="flex items-center gap-3">
                    <Link
                      to={`/geo/${c.code}`}
                      className="flex w-40 shrink-0 items-center gap-1.5 truncate text-sm text-secondary hover:text-primary"
                    >
                      <span>{c.flag || '🌐'}</span>
                      <span className="truncate">{c.name}</span>
                    </Link>
                    <div className="relative h-5 flex-1 overflow-hidden rounded bg-surface-3">
                      <div
                        className="h-full rounded bg-accent/70"
                        style={{ width: maxUsers ? `${Math.max(2, (c.users / maxUsers) * 100)}%` : '0%' }}
                      />
                    </div>
                    <span className="w-16 shrink-0 text-right font-mono text-sm tabular text-primary">
                      {c.users.toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>

        <div className="mt-3">
          <SmsSummaryCard />
        </div>
      </div>
    </>
  )
}

function KPI({
  label,
  value,
  loading,
  accent,
}: {
  label: string
  value: number | undefined
  loading: boolean
  accent?: boolean
}) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="text-xs text-tertiary">{label}</div>
      {loading ? (
        <Skeleton className="mt-1.5 h-7 w-16" />
      ) : (
        <div className={`mt-0.5 font-mono text-2xl tabular ${accent ? 'text-accent' : 'text-primary'}`}>
          {value?.toLocaleString() ?? '—'}
        </div>
      )}
    </div>
  )
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-border-default bg-surface-2 p-3">
      <div className="mb-2 text-sm font-medium text-secondary">{title}</div>
      {children}
    </div>
  )
}
